import { ReactNode, createContext, useMemo } from 'react';
import useLoanTypes from '~/hooks/useLoanTypes';
import useLocalStorage from '~/hooks/useLocalStorage';

export const defaultFromToRange = { from: '', to: '' };

export type TpoAssignmentInterval = {
  volume: typeof defaultFromToRange;
  quantity: typeof defaultFromToRange;
};

type TpoLoanTypeKey = `tpo_${string}`;

export type LoanOfficerSearchParams = {
  does_tpo: boolean;
  fullname: string;
  nmls: string;
  license_area: string[];
  transaction_address: {
    state: StateAlias | EmptyString;
    zip: string;
    city: string;
    county: string;
  };
  office: {
    position?: string;
    branch_manager: boolean;
    blank_position: boolean;
    name?: string;
    license?: string;
    nmls?: string;
    type?: string[];
    strict?: boolean;
    address: {
      state: StateAlias | EmptyString;
      zip: string;
      city: string;
      county: string;
    };
  };
  period: Period;
  financial_data: {
    loan_and_transaction: {
      loan_volume: ParamInterval;
      transaction_volume: ParamInterval;
      tpo_assignment: TpoAssignmentInterval;
    };
    loan_type: {
      fha?: ParamInterval;
      va?: ParamInterval;
      pp?: ParamInterval;
      other?: ParamInterval;
      sba?: ParamInterval;
      cnv?: ParamInterval;
      rev?: ParamInterval;
    };
    tpo_loan_type: Record<TpoLoanTypeKey, ParamInterval>;
    transaction_type: {
      purchase: ParamInterval;
      refinance: ParamInterval;
      construction: ParamInterval;
      heloc: ParamInterval;
    };
    tpo_transaction_type: {
      tpo_purchase: ParamInterval;
      tpo_refinance: ParamInterval;
      tpo_construction: ParamInterval;
      tpo_heloc: ParamInterval;
    };
  };
};

export const defaultAmountQuantityRange = {
  amount: defaultFromToRange,
  quantity: defaultFromToRange,
};

export type LoanOfficersSearchParamsContextState = {
  searchParams: LoanOfficerSearchParams;
  defaultValues: LoanOfficerSearchParams;
  setSearchParams: (newParams: LoanOfficerSearchParams) => void;
};

const initialState: LoanOfficersSearchParamsContextState = {
  defaultValues: {} as LoanOfficerSearchParams,
  searchParams: {} as LoanOfficerSearchParams,
  setSearchParams: () => {},
};

type LoanOfficersSearchParamsProviderProps = {
  children: ReactNode;
};

const LoanOfficersSearchParamsContext = createContext(initialState);

function LoanOfficersSearchParamsProvider({ children }: LoanOfficersSearchParamsProviderProps) {
  const { loanTypes } = useLoanTypes();

  const defaultValues: LoanOfficerSearchParams = useMemo(
    () => ({
      does_tpo: false,
      fullname: '',
      nmls: '',
      license_area: [],
      transaction_address: {
        state: '',
        city: '',
        zip: '',
        county: '',
      },
      office: {
        name: '',
        position: '',
        branch_manager: false,
        blank_position: false,
        license: '',
        type: [],
        strict: false,
        address: {
          state: '',
          city: '',
          zip: '',
          county: '',
        },
      },
      period: 14,
      financial_data: {
        loan_and_transaction: {
          loan_volume: defaultAmountQuantityRange,
          transaction_volume: defaultAmountQuantityRange,
          tpo_assignment: {
            volume: defaultFromToRange,
            quantity: defaultFromToRange,
          },
        },
        loan_type: Object.fromEntries(loanTypes.map((type) => [type, defaultAmountQuantityRange])),
        tpo_loan_type: Object.fromEntries(
          loanTypes.map((type) => [`tpo_${type}`, defaultAmountQuantityRange])
        ),
        transaction_type: {
          purchase: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
          refinance: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
          construction: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
          heloc: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
        },
        tpo_transaction_type: {
          tpo_purchase: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
          tpo_refinance: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
          tpo_construction: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
          tpo_heloc: {
            ...defaultAmountQuantityRange,
            percentage: defaultFromToRange,
          },
        },
      },
    }),
    [loanTypes]
  );

  const [searchParams, setSearchParams] = useLocalStorage<LoanOfficerSearchParams>(
    'searchParams:loanOfficers',
    defaultValues
  );

  return (
    <LoanOfficersSearchParamsContext.Provider
      value={{ searchParams, setSearchParams, defaultValues }}
    >
      {children}
    </LoanOfficersSearchParamsContext.Provider>
  );
}

export { LoanOfficersSearchParamsProvider, LoanOfficersSearchParamsContext };
